import { z } from 'zod';

import { contactEmailFor, type ListingDetails, listingPhotoUrl } from './listingDetails';

/**
 * What the confirmation page shows about the just-submitted offer, carried
 * through sessionStorage — `/make-offer/confirm` has no slug in its URL (the
 * legacy route shape), so it cannot re-fetch the listing on its own.
 */
const offerConfirmationSchema = z.object({
  slug: z.string(),
  offerId: z.number(),
  priceCents: z.number().int(),
  address: z.string(),
  photoUrl: z.string().nullable(),
  partnerName: z.string().nullable(),
  partnerEmail: z.string(),
  partnerPhone: z.string().nullable(),
  storedAt: z.number(),
});

export type OfferConfirmation = z.infer<typeof offerConfirmationSchema>;

const KEY = 'opendoor.makeOffer.offerConfirmation';

// Long enough to survive a refresh or a brief detour, short enough that a
// later visit in the same tab doesn't replay an old offer as "just now".
const MAX_AGE_MS = 30 * 60 * 1000;

export const confirmationFromSubmission = (
  slug: string,
  offerId: number,
  priceCents: number,
  listing: ListingDetails,
): Omit<OfferConfirmation, 'storedAt'> => ({
  slug,
  offerId,
  priceCents,
  address: listing.address.human_readable,
  photoUrl: listingPhotoUrl(listing) ?? null,
  partnerName: listing.resale_agent_lead?.full_name ?? null,
  partnerEmail:
    listing.resale_agent_lead?.external_email ??
    listing.resale_agent_lead?.email ??
    contactEmailFor(listing),
  // The serializer's phone_number is the market-routed external line (already
  // falling back to the display phone); display_phone_number skips the
  // routing, so it is only the last resort.
  partnerPhone:
    listing.resale_agent_lead?.phone_number ??
    listing.resale_agent_lead?.display_phone_number ??
    null,
});

export const storeOfferConfirmation = (confirmation: Omit<OfferConfirmation, 'storedAt'>): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ ...confirmation, storedAt: Date.now() }));
  } catch {
    // Storage can be unavailable; the confirm page then sends the agent back
    // to the start rather than rendering an empty shell.
  }
};

export const readOfferConfirmation = (): OfferConfirmation | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) {
      return undefined;
    }
    const confirmation = offerConfirmationSchema.parse(JSON.parse(raw));
    return Date.now() - confirmation.storedAt <= MAX_AGE_MS ? confirmation : undefined;
  } catch {
    return undefined;
  }
};
